import React from "react";
import { ArrowRight } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";

const ThankYou = () => {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const from = params.get("from") || "application"; // default to 'application'

  // Message variants
  const variants = {
    application: {
      heading: "Application",
      subheading: "Received",
      message: (
        <>
          Thank you for applying to our AI course.
          <br />
          <span className="text-white/60">
            Your journey into the future of intelligence begins here.
          </span>
        </>
      ),
      label: "AI Course Application",
    },
    contact: {
      heading: "Message",
      subheading: "Sent",
      message: (
        <>
          Thank you for reaching out to Frost &amp; Grey.
          <br />
          <span className="text-white/60">
            Our team will respond to your inquiry shortly.
          </span>
        </>
      ),
      label: "Contact Request",
    },
    consultation: {
      heading: "Consultation",
      subheading: "Booked",
      message: (
        <>
          Thank you for booking a consultation.
          <br />
          <span className="text-white/60">
            Our experts will be in touch to confirm your session.
          </span>
        </>
      ),
      label: "Strategy Session",
    },
  };

  // Choose variant based on "from"
  const { heading, subheading, message } =
    variants[from] || variants.application;

  return (
    <div className="min-h-screen bg-black text-white font-light">
      <Helmet>
        <title>Thank You | Frost & Grey</title>

        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />

        <meta
          name="description"
          content="Thank you for connecting with Frost & Grey. We’ll reach out shortly regarding your request."
        />
      </Helmet>

      {/* Hero Section - Editorial Thank You */}
      <div className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-5 pointer-events-none">
          <div
            className="w-full h-full"
            style={{
              backgroundImage: `radial-gradient(circle at 40px 40px, white 2px, transparent 2px)`,
              backgroundSize: "80px 80px",
            }}
          ></div>
        </div>
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black opacity-90"></div>

        <div className="relative z-10 text-center max-w-2xl mx-auto px-6 py-24 mt-8">
          <div className="mb-12">
            <p className="text-[10px] sm:text-xs md:text-[11px] tracking-[0.3em] uppercase text-gray-400 mb-6">
              Frost &amp; Grey AI
            </p>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-thin tracking-tight leading-tight mb-8">
              <span className="block">{heading}</span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-400">
                {subheading}
              </span>
            </h1>
            <div className="w-24 h-px bg-gradient-to-r from-white to-transparent mx-auto mb-8"></div>
            <p className="text-lg sm:text-xl md:text-2xl font-light leading-relaxed tracking-wide text-white/90 mb-10">
              {message}
            </p>
            <div className="flex flex-col gap-6 items-center justify-center mt-10">
              <Link
                to="/"
                className="group border border-white px-10 py-4 text-[11px] sm:text-sm tracking-[0.2em] uppercase rounded-lg hover:bg-white hover:text-black transition-all duration-300 mb-2"
              >
                <span className="group-hover:tracking-[0.3em] transition-all duration-300 flex items-center justify-center gap-2">
                  Back to Home
                  <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
              <Link
                to="/programs"
                className="text-[11px] sm:text-sm tracking-[0.2em] uppercase text-gray-400 hover:text-white transition-colors"
              >
                Explore More Programs →
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Editorial Quote */}
      <div className="bg-white text-black">
        <div className="max-w-3xl mx-auto px-6 py-12">
          <div className="text-center">
            <div className="text-5xl sm:text-6xl md:text-7xl font-thin text-gray-800 mb-2">
              "
            </div>
            <blockquote className="text-xl sm:text-2xl md:text-3xl font-light leading-relaxed tracking-wide text-black/80">
              The future belongs to those who are{" "}
              <span className="text-gray-500">bold enough</span> to learn,
              adapt, and lead with intelligence.
            </blockquote>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThankYou;
